import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const BenefitsSection = styled.section`
  padding: 120px 0;
  background: 
    radial-gradient(ellipse at center bottom, #1a1a1a 0%, #0a0a0a 70%),
    linear-gradient(0deg, #000 0%, #0f0f0f 50%, #000 100%);
  position: relative;
  overflow: hidden;

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, 
      transparent 0%, 
      rgba(255,215,0,0.4) 30%, 
      rgba(255,180,0,0.6) 50%, 
      rgba(255,215,0,0.4) 70%, 
      transparent 100%);
    box-shadow: 0 0 15px rgba(255,215,0,0.3);
  }

  &:after {
    content: '';
    position: absolute;
    bottom: 50%;
    left: -50%;
    right: -50%;
    top: -50%;
    background: 
      radial-gradient(ellipse 120% 80% at 50% 100%, rgba(255,255,255,0.02) 0%, transparent 60%);
    animation: luxuryAmbient 10s ease-in-out infinite alternate;
  }

  @keyframes luxuryAmbient {
    0% { opacity: 0.3; }
    100% { opacity: 0.7; }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Title = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  color: ${props => props.theme.colors.white};
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const BenefitCard = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.mediumGray}40, ${props => props.theme.colors.darkGray}60);
  border: 1px solid ${props => props.theme.colors.success}30;
  border-radius: 16px;
  padding: 40px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.4s ease;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 24px;
    border-radius: 12px;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.success}, ${props => props.theme.colors.primary});
    transition: all 0.4s ease;
  }

  @media (hover: hover) {
    &:hover {
      transform: translateY(-10px) scale(1.02);
      border-color: ${props => props.theme.colors.success}80;
      box-shadow: 0 25px 50px rgba(0, 255, 136, 0.25);

      &:before {
        height: 8px;
        box-shadow: 0 0 20px ${props => props.theme.colors.success}60;
      }
    }
  }

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    &:active {
      transform: scale(0.98);
      border-color: ${props => props.theme.colors.success}60;
    }
  }
`;

const BenefitIcon = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.theme.colors.success}20, ${props => props.theme.colors.primary}20);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 24px;
  transition: all 0.4s ease;
`;

const BenefitTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.white};
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const BenefitDescription = styled.p`
  color: ${props => props.theme.colors.lightGray};
  line-height: 1.6;
  margin-bottom: 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.95rem;
  }
`;

const BenefitMetric = styled(motion.div)`
  background: ${props => props.theme.colors.success}10;
  border: 1px solid ${props => props.theme.colors.success}30;
  border-radius: 8px;
  padding: 16px;
  color: ${props => props.theme.colors.success};
  font-weight: 600;
  font-size: 1rem;
  text-align: center;
  transition: all 0.3s ease;
`;

const Benefits = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const benefits = [
    {
      icon: "1",
      title: "Módulo 1: Fundamentos da LM Motors",
      description: "Conheça a realidade do cenário automotivo, as vantagens da consignação e por que precisa existir um PROPÓSITO para consignação.",
      metric: "Base sólida para o negócio"
    },
    {
      icon: "2",
      title: "Módulo 2: Marketing e Negociação",
      description: "Marketing da atração, técnicas de negociação com cliente, o poder da AUTORIDADE e como gerar leads qualificados.",
      metric: "Clientes qualificados sempre"
    },
    {
      icon: "3",
      title: "Módulo 3: Consignação Lucrativa",
      description: "Passo a passo de como consignar sem dor de cabeça tendo LUCRO, como escolher o carro certo e calcular a margem ideal.",
      metric: "Lucro garantido sem riscos"
    },
    {
      icon: "$",
      title: "Margem Ideal na Consignação",
      description: "Aprenda a calcular exatamente qual margem aplicar em cada veículo para maximizar lucros sem perder vendas.",
      metric: "Margem otimizada sempre"
    },
    {
      icon: "⚡",
      title: "Resolução de Problemas",
      description: "Como resolver problemas sem deixar problema! Técnicas para lidar com situações complexas na consignação.",
      metric: "Zero dor de cabeça"
    },
    {
      icon: "▶",
      title: "Método CCP Completo",
      description: "Sistema completo de Consignação com Propósito que transforma sua loja em uma máquina de lucro sustentável.",
      metric: "Transformação total"
    }
  ];

  return (
    <BenefitsSection ref={ref}>
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
O QUE VOCÊ VAI APRENDER NO CURSO CCP
          </Title>
        </Header>

        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -8, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <BenefitIcon
                whileHover={{ scale: 1.15, rotate: 5 }}
                transition={{ duration: 0.3 }}
              >
                {benefit.icon}
              </BenefitIcon>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitDescription>{benefit.description}</BenefitDescription>
              <BenefitMetric
                whileHover={{ scale: 1.05 }}
                transition={{ duration: 0.2 }}
              >
                {benefit.metric}
              </BenefitMetric>
            </BenefitCard>
          ))}
        </BenefitsGrid>
        
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8, delay: 0.8 }}
          style={{
            marginTop: '80px',
            textAlign: 'center',
            position: 'relative'
          }}
        >
          <img 
            src="/Braços abertos.jpg" 
            alt="Leonardo Celini - Pronto para transformar seu negócio" 
            style={{
              maxWidth: '400px',
              width: '100%',
              borderRadius: '20px',
              boxShadow: '0 20px 60px rgba(0, 212, 255, 0.3)'
            }}
          />
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 1 }}
            style={{
              marginTop: '24px',
              fontSize: '1.5rem',
              fontWeight: '600',
              color: '#FFFFFF'
            }}
          >
            PRONTO PARA TRANSFORMAR SEU NEGÓCIO?
          </motion.h3>
        </motion.div>
      </Container>
    </BenefitsSection>
  );
};

export default Benefits;